import {
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  inject,
  OnInit,
} from '@angular/core';
import { takeUntilDestroyed, toSignal } from '@angular/core/rxjs-interop';
import { AsyncPipe, NgIf } from '@angular/common';
import { RouterOutlet } from '@angular/router';
import { SidebarComponent } from '../sidebar/sidebar.component';
import { Store } from '@ngrx/store';
import { AppState } from '../../store/app.state';
import { userSettingsFeature } from '../../store/state/user-settings/user-settings.feature';
import { UiButtonComponent, UiHeaderWithBreadcrumbsComponent } from '@ui';
import { routeFeature } from '../../store/state/route/route.feature';
import { RbacOrgAllowDirective } from '../../directives/rbac-org-allow.directive';
import { TranslatePipe } from '@ngx-translate/core';
import { map } from 'rxjs';
import { organizationFeature } from '../../store/state/organization/organization.feature';
import { OrganizationActions } from '../../store/state/organization/organization.actions';
import { PermissionModels } from '@share-models';
import { menuFeature } from '../../store/state/menu/menu.feature';
import { MenuKey } from '../../enums/menu.enum';
import { LetDirective } from '@ngrx/component';

@Component({
  selector: 'app-layout',
  templateUrl: './layout.component.html',
  styleUrl: './layout.component.scss',
  imports: [
    RouterOutlet,
    SidebarComponent,
    UiHeaderWithBreadcrumbsComponent,
    UiButtonComponent,
    RbacOrgAllowDirective,
    TranslatePipe,
    AsyncPipe,
    NgIf,
    LetDirective,
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class LayoutComponent implements OnInit {
  private readonly destroyRef = inject(DestroyRef);
  private readonly store = inject(Store<AppState>);

  public readonly breadcrumbs$ = this.store.select(
    routeFeature.selectBreadcrumbs,
  );
  public readonly permissions = PermissionModels.Permissions;

  public readonly isSaving$ = this.store.select(
    organizationFeature.selectIsSaving,
  );
  public readonly isLoading$ = this.store
    .select(organizationFeature.selectActiveOrganizationDocument)
    .pipe(map((org) => org === null));

  public readonly showOrgSettingsSave = toSignal(
    this.store
      .select(menuFeature.selectActiveLevel2)
      .pipe(map((activeLevel2) => activeLevel2 === MenuKey.details)),
    { initialValue: false },
  );

  private readonly darkThemeClassName = 'dark-theme';
  private readonly lightThemeClassName = 'light-theme';

  public ngOnInit(): void {
    this.store
      .select(userSettingsFeature.selectTheme)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((theme) => {
        this.syncBodyTheme(theme);
      });
  }

  public save(): void {
    this.store.dispatch(OrganizationActions.pressSave());
  }

  private syncBodyTheme(theme: 'light' | 'dark'): void {
    const isDarkTheme = theme === 'dark';
    document.body.classList.toggle(this.darkThemeClassName, isDarkTheme);
    document.body.classList.toggle(this.lightThemeClassName, !isDarkTheme);
  }
}
